import { Directive, ElementRef, NgZone, OnDestroy, OnInit, Input, AfterViewInit } from '@angular/core';
import { ScrollDispatcher, CdkScrollable } from '@angular/cdk/scrolling';
import { fromEvent, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Phoenix Scrollable Directive
 * 
 * A simplified version of Angular CDK's CdkScrollable that registers the element 
 * (or optionally a child element with a specific selector) with ScrollDispatcher 
 * to enable proper overlay positioning (tooltips, dropdowns, etc.) when scrolling occurs.
 * 
 * Usage:
 * <div phx-scrollable>
 *   <!-- scrollable content -->
 * </div>
 * 
 * Or for nested scrollable elements:
 * <div phx-scrollable phxScrollElementSelector=".ant-table-body">
 *   <!-- content with .ant-table-body element that actually scrolls -->
 * </div>
 */
@Directive({
  selector: '[phx-scrollable]',
  exportAs: 'phxScrollable'
})
export class PhxScrollableDirective implements AfterViewInit, OnDestroy {
  @Input() phxScrollElementSelector?: string;
  
  private _cdkScrollable: CdkScrollable;
  private _scrollableElement: HTMLElement;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private scrollDispatcher: ScrollDispatcher,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.setupScrollable();
  }

  ngOnDestroy(): void {
    // Clean up: let CdkScrollable handle its own cleanup
    if (this._cdkScrollable) {
      this._cdkScrollable.ngOnDestroy();
    }
  }

  private setupScrollable(): void {
    // Determine which element to make scrollable
    if (this.phxScrollElementSelector) {
      // Look for a child element with the specified selector
      this._scrollableElement = this.elementRef.nativeElement.querySelector(this.phxScrollElementSelector);
      
      if (!this._scrollableElement) {
        this._scrollableElement = this.elementRef.nativeElement;
      } 
    } else {
      // Use the host element
      this._scrollableElement = this.elementRef.nativeElement;
    }

    // Create ElementRef for the scrollable element
    const scrollableElementRef = new ElementRef(this._scrollableElement);

    // Create a CdkScrollable instance to work with ScrollDispatcher
    this._cdkScrollable = new CdkScrollable(
      scrollableElementRef,
      this.scrollDispatcher,
      this.ngZone
    );

    // Initialize the CdkScrollable
    this._cdkScrollable.ngOnInit();
  }
}
